import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Send, Mail, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = ({ selectedPackage, setSelectedPackage }) => {
  const { t, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        package_interest: selectedPackage || null,
      });

      toast.success(t(translations.contact.form.success));
      setFormData({ name: '', email: '', company: '', message: '' });
      setSelectedPackage('');
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t(translations.contact.form.error));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const packageOptions = ['Starter', 'Professional', 'Enterprise'];

  return (
    <section 
      id="contact" 
      className="section-padding bg-[#0a0a0a] relative overflow-hidden"
      data-testid="contact-section"
    >
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-emerald-500/30 to-transparent" />
      <div className="absolute right-0 bottom-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl" />

      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            data-testid="contact-title"
          >
            {t(translations.contact.sectionTitle)}
          </h2>
          <p className="text-base md:text-lg text-white/60 max-w-2xl mx-auto">
            {t(translations.contact.sectionSubtitle)}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div className="p-6 rounded-2xl bg-[#111111] border border-white/10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-white/50">{language === 'en' ? 'Email Us' : 'Email'}</p>
                  <a 
                    href="mailto:b2b@360gamingsolutions.com"
                    className="text-emerald-500 hover:text-emerald-400 transition-colors font-medium"
                    data-testid="contact-email"
                  >
                    b2b@360gamingsolutions.com
                  </a>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20">
              <h4 className="font-semibold text-white mb-3">
                {language === 'en' ? 'Why Partner With Us?' : 'Γιατί να Συνεργαστείτε Μαζί Μας;'}
              </h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {language === 'en' ? '15+ years industry experience' : '15+ χρόνια εμπειρία στον κλάδο'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {language === 'en' ? 'Proven track record' : 'Αποδεδειγμένο ιστορικό επιτυχίας'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {language === 'en' ? 'Tailored solutions' : 'Προσαρμοσμένες λύσεις'}
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {language === 'en' ? '24/7 dedicated support' : '24/7 αποκλειστική υποστήριξη'}
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <form 
              onSubmit={handleSubmit}
              className="contact-form space-y-5 p-8 rounded-2xl bg-[#111111] border border-white/10"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-white/60 mb-2">
                    {t(translations.contact.form.name)} *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/30 focus:border-emerald-500"
                    placeholder="John Doe"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">
                    {t(translations.contact.form.email)} *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/30 focus:border-emerald-500"
                    placeholder="john@company.com"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-white/60 mb-2">
                    {t(translations.contact.form.company)}
                  </label>
                  <Input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/30 focus:border-emerald-500"
                    placeholder="Company Name"
                    data-testid="contact-company-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-white/60 mb-2">
                    {t(translations.contact.form.package)}
                  </label>
                  <Select 
                    value={selectedPackage} 
                    onValueChange={setSelectedPackage}
                  >
                    <SelectTrigger 
                      className="bg-[#0a0a0a] border-white/10 text-white focus:border-emerald-500"
                      data-testid="contact-package-select"
                    >
                      <SelectValue placeholder={t(translations.contact.form.selectPackage)} />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      {packageOptions.map((pkg) => (
                        <SelectItem 
                          key={pkg} 
                          value={pkg}
                          className="text-white hover:bg-emerald-500/20 focus:bg-emerald-500/20 focus:text-white cursor-pointer"
                        >
                          {pkg}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-white/60 mb-2">
                  {t(translations.contact.form.message)} *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-[#0a0a0a] border-white/10 text-white placeholder:text-white/30 focus:border-emerald-500 resize-none"
                  placeholder={language === 'en' ? 'Tell us about your needs...' : 'Πείτε μας για τις ανάγκες σας...'}
                  data-testid="contact-message-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold py-6 transition-all duration-300"
                data-testid="contact-submit-button"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {t(translations.contact.form.submit)}
                    <Send className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
